import {PlatformDataType, SETTINGS_OF_PLATFORM} from 'src/interfaces/Platform.interface'
import {normalizeMappingGroupCode} from 'src/utils/fieldMappingSaveUtils'
import {
  getIdentifierFieldKeysForRolePair,
  type ProductIdentifierPair,
} from 'src/utils/productIdentifierResolveUtils'

type RelocationStoreSnapshot = {
  mainStoreData: PlatformDataType | null
  omsStoreData: PlatformDataType | null
  shippingStoreData: PlatformDataType | null
}

export type {ProductIdentifierPair, RelocationStoreSnapshot}

export type ProductIdentifierMappingPayload = {
  productIdentifiers: ProductIdentifierPair[]
  legacyScalars: Record<string, any>
}

const normalizeRole = (role?: string | null): string => `${role ?? ''}`.trim().toUpperCase()

const normalizeIdentifierValue = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  return `${value}`.trim()
}

const applyLegacyIdentifierScalars = (
  payload: Record<string, any>,
  productIdentifiers: ProductIdentifierPair[]
) => {
  const appliedRolePairs = new Set<string>()

  productIdentifiers.forEach((pair) => {
    const selectedRole = normalizeRole(pair.platform_identifier1)
    const relatedRole = normalizeRole(pair.platform_identifier2)
    const rolePairKey = [selectedRole, relatedRole].sort().join(':')
    if (appliedRolePairs.has(rolePairKey)) return

    const selectedValue = normalizeIdentifierValue(pair.platform_product_identifier1)
    const relatedValue = normalizeIdentifierValue(pair.platform_product_identifier2)

    getIdentifierFieldKeysForRolePair(selectedRole, relatedRole).forEach((field) => {
      payload[field] = selectedValue
    })
    getIdentifierFieldKeysForRolePair(relatedRole, selectedRole).forEach((field) => {
      payload[field] = relatedValue
    })

    appliedRolePairs.add(rolePairKey)
  })
}

/** Form containers that belong on OMS when relocation is active — never written to shipping. */
export const RELOCATED_OMS_SETTING_CONTAINER_KEYS = ['shipment_setting', 'oms_order_setting'] as const

export const isRelocatedOmsSettingContainerKey = (key: string): boolean =>
  (RELOCATED_OMS_SETTING_CONTAINER_KEYS as readonly string[]).includes(key)

/** Schema `SHIPMENT` and form `shipment_setting` are the same relocated section. */
export const isRelocatedShipmentMappingGroup = (groupCode?: string | null): boolean =>
  normalizeMappingGroupCode(groupCode ?? '') === 'SHIPMENT'

/** Scalar / pair keys written for the relocated OMS↔Shipping identifier — excluded from shipping save. */
export const RELOCATED_IDENTIFIER_SCALAR_KEYS = [
  'oms_identifier',
  'oms_identifier_primary',
  'oms_product_identifier',
  'primary_oms_identifier',
  'secondary_oms_identifier',
  'primary_oms_product_identifier',
  'secondary_oms_product_identifier',
  'shipping_identifier',
  'shipping_identifier_primary',
  'primary_shipping_identifier',
  'secondary_shipping_identifier',
  'product_identifiers',
] as const

/** OMS is hub (no main store) and a shipping platform is connected. */
export const isOmsHubWithShippingRelocationScope = ({
  mainStoreData,
  omsStoreData,
  shippingStoreData,
}: RelocationStoreSnapshot): boolean =>
  Boolean(omsStoreData && shippingStoreData && !mainStoreData)

export const shouldSwapOmsShippingIdentifierLayout = (
  snapshot: RelocationStoreSnapshot | undefined,
  selectedRole: string,
  relatedRole: string
): boolean =>
  !!snapshot &&
  isOmsHubWithShippingRelocationScope(snapshot) &&
  normalizeRole(selectedRole) === 'SHIPPING' &&
  normalizeRole(relatedRole) === 'OMS'

export const isOnOmsChannelSettingsPage = (
  selectedPlatformId: number,
  omsStoreData: PlatformDataType | null | undefined
): boolean => Number(omsStoreData?.id) === selectedPlatformId

export const isOnShippingChannelSettingsPage = (
  selectedPlatformId: number,
  shippingStoreData: PlatformDataType | null | undefined
): boolean => Number(shippingStoreData?.id) === selectedPlatformId

/** Append OMS SHIPMENT schema sections onto the shipping page section list. */
export const mergeOmsShipmentSectionsIntoPlatformSettings = (
  shippingPlatformSettings: SETTINGS_OF_PLATFORM[],
  omsPlatformSettings: SETTINGS_OF_PLATFORM[] | undefined | null
): SETTINGS_OF_PLATFORM[] => {
  const omsShipmentSections = (omsPlatformSettings ?? []).filter(
    (setting) => setting?.group_code === 'SHIPMENT'
  )

  if (!omsShipmentSections.length) {
    return shippingPlatformSettings
  }

  const withoutShipment = shippingPlatformSettings.filter(
    (setting) => setting?.group_code !== 'SHIPMENT'
  )

  return [...withoutShipment, ...omsShipmentSections]
}

/**
 * Ensures the OMS↔Shipping relocated SHIPMENT block has a default shipping source selected
 * if none was previously saved on the OMS hub. Gracefully defaults to the first available source
 * where `isShippingStore === true`.
 */
export const resolveDefaultRelocatedShipmentSource = (
  savedShipmentSetting: Record<string, any> | undefined | null,
  platformSettingsDefinitions: SETTINGS_OF_PLATFORM[]
): Record<string, any> => {
  const currentSetting = savedShipmentSetting ?? {}

  // If a source is already selected and saved, leave it exactly as is (no regression)
  if (currentSetting?.selected_source_platform?.id) {
    return currentSetting
  }

  const shipmentDefinition = platformSettingsDefinitions.find(
    (setting) => setting?.group_code === 'SHIPMENT'
  )
  const availableSources = Array.isArray((shipmentDefinition as any)?.available_shipping_sources)
    ? (shipmentDefinition as any).available_shipping_sources
    : []

  // Look for the first source in the schema that is explicitly marked as a shipping store
  const defaultSource = availableSources.find(
    (source: any) => source?.isShippingStore === true
  )

  if (defaultSource) {
    return {
      ...currentSetting,
      selected_source_platform: defaultSource,
    }
  }

  // Fallback: if no matching source is found or array is empty, return unchanged (safeguard)
  return currentSetting
}

export const isOmsShippingIdentifierPair = (
  pair: ProductIdentifierPair,
  omsPlatformId: number,
  shippingPlatformId: number
): boolean => {
  const platform1 = Number(pair.platform1)
  const platform2 = Number(pair.platform2)
  const platformsMatch =
    (platform1 === omsPlatformId && platform2 === shippingPlatformId) ||
    (platform1 === shippingPlatformId && platform2 === omsPlatformId)

  if (!platformsMatch) return false

  const roles = [
    normalizeRole(pair.platform_identifier1),
    normalizeRole(pair.platform_identifier2),
  ]
  return roles.includes('OMS') && roles.includes('SHIPPING')
}

/**
 * Ensures the OMS↔Shipping mapping pair is OMS-centric before saving to the OMS record.
 * Swaps BOTH the platform IDs and the role/product-identifier strings so that:
 *   platform1 = OMS platform ID, platform_identifier1 = "OMS"
 *   platform2 = Shipping platform ID, platform_identifier2 = "SHIPPING"
 */
export const reorientIdentifierPairToOmsCentric = (
  pair: ProductIdentifierPair
): ProductIdentifierPair => {
  if (normalizeRole(pair.platform_identifier1) === 'OMS') {
    return pair
  }

  // pair currently has: platform1=Shipping, platform_identifier1="SHIPPING"
  // We need:            platform1=OMS,      platform_identifier1="OMS"
  return {
    ...pair,
    platform1: pair.platform2,
    platform2: pair.platform1,
    platform_identifier1: pair.platform_identifier2,
    platform_product_identifier1: pair.platform_product_identifier2,
    platform_identifier2: pair.platform_identifier1,
    platform_product_identifier2: pair.platform_product_identifier1,
  }
}

export const findOmsShippingIdentifierPair = (
  pairs: ProductIdentifierPair[] | undefined | null,
  omsPlatformId: number,
  shippingPlatformId: number
): ProductIdentifierPair | undefined =>
  (pairs ?? []).find((pair) => isOmsShippingIdentifierPair(pair, omsPlatformId, shippingPlatformId))

type BuildOmsRelocationSavePayloadArgs = {
  omsPlatformId: number
  shippingPlatformId: number
  omsHubPlatformSettings: Record<string, any>
  formValues: Record<string, any>
  identifierMappingPayload?: ProductIdentifierMappingPayload | null
}

/** Build the OMS platform_settings write for relocated SHIPMENT + OMS↔Shipping identifier sections. */
export const buildOmsRelocationSavePayload = ({
  omsPlatformId,
  shippingPlatformId,
  omsHubPlatformSettings,
  formValues,
  identifierMappingPayload,
}: BuildOmsRelocationSavePayloadArgs): Record<string, any> => {
  const hubPairs = Array.isArray(omsHubPlatformSettings.product_identifiers)
    ? (omsHubPlatformSettings.product_identifiers as ProductIdentifierPair[])
    : []
  const preservedPairs = hubPairs.filter(
    (pair) => !isOmsShippingIdentifierPair(pair, omsPlatformId, shippingPlatformId)
  )
  const existingHubPair = findOmsShippingIdentifierPair(
    hubPairs,
    omsPlatformId,
    shippingPlatformId
  )

  const candidatePairs = [
    ...(identifierMappingPayload?.productIdentifiers ?? []),
    ...(Array.isArray(formValues.product_identifiers) ? formValues.product_identifiers : []),
  ]
  const shippingCentricPair = findOmsShippingIdentifierPair(
    candidatePairs,
    omsPlatformId,
    shippingPlatformId
  )

  let nextOmsShippingPair: ProductIdentifierPair | undefined
  if (shippingCentricPair) {
    nextOmsShippingPair = reorientIdentifierPairToOmsCentric({...shippingCentricPair})
    if (existingHubPair?.id) nextOmsShippingPair.id = existingHubPair.id
    if (existingHubPair?.platform_setting) {
      nextOmsShippingPair.platform_setting = existingHubPair.platform_setting
    }
  } else if (existingHubPair) {
    nextOmsShippingPair = {...existingHubPair}
  }

  const payload: Record<string, any> = {
    platformId: omsPlatformId,
    ...omsHubPlatformSettings,
  }

  RELOCATED_OMS_SETTING_CONTAINER_KEYS.forEach((key) => {
    if (formValues[key] !== undefined) {
      payload[key] = formValues[key]
    }
  })

  payload.product_identifiers = nextOmsShippingPair
    ? [...preservedPairs, nextOmsShippingPair]
    : preservedPairs

  applyLegacyIdentifierScalars(
    payload,
    Array.isArray(payload.product_identifiers) ? payload.product_identifiers : []
  )

  return payload
}

type BuildShippingNativeSavePayloadArgs = {
  shippingPlatformId: number
  shippingSavedPlatformSettings: Record<string, any>
  formValues: Record<string, any>
}

const isNativeSettingContainerKey = (key: string): boolean =>
  key.endsWith('_setting') && !isRelocatedOmsSettingContainerKey(key)

const bindMappingToHubFieldMapping = (mapping: any, hubFieldMappings: any[]): any => {
  const settingMappingId = Number(mapping?.setting_mapping_id)
  if (!Number.isFinite(settingMappingId)) return mapping

  const hubMatch = hubFieldMappings.find((hub) => {
    if (Number(hub?.setting_mapping_id) !== settingMappingId) return false
    if (mapping?.isDefault === undefined) return true
    return Boolean(hub?.isDefault) === Boolean(mapping.isDefault)
  })

  if (!hubMatch?.id) return mapping

  return {
    ...mapping,
    field_mapping_id: hubMatch.id,
    savedMappingId: hubMatch.id,
  }
}

/** Build the shipping platform write with only native sections (excludes relocated OMS data). */
export const buildShippingNativeSavePayload = ({
  shippingPlatformId,
  shippingSavedPlatformSettings,
  formValues,
}: BuildShippingNativeSavePayloadArgs): Record<string, any> => {
  const payload: Record<string, any> = {
    platformId: shippingPlatformId,
    ...shippingSavedPlatformSettings,
  }

  Object.keys(formValues).forEach((key) => {
    if (!isNativeSettingContainerKey(key)) return
    payload[key] = formValues[key]
  })

  RELOCATED_IDENTIFIER_SCALAR_KEYS.forEach((key) => {
    if (shippingSavedPlatformSettings[key] !== undefined) {
      payload[key] = shippingSavedPlatformSettings[key]
    } else {
      delete payload[key]
    }
  })

  RELOCATED_OMS_SETTING_CONTAINER_KEYS.forEach((key) => {
    delete payload[key]
  })

  if (formValues.identifier_setting !== undefined) {
    const preservedIdentifierSetting =
      typeof shippingSavedPlatformSettings.identifier_setting === 'object'
        ? {...shippingSavedPlatformSettings.identifier_setting}
        : {}
    const formIdentifierSetting =
      typeof formValues.identifier_setting === 'object' ? formValues.identifier_setting : {}

    Object.keys(formIdentifierSetting).forEach((key) => {
      if ((RELOCATED_IDENTIFIER_SCALAR_KEYS as readonly string[]).includes(key)) return
      preservedIdentifierSetting[key] = formIdentifierSetting[key]
    })

    payload.identifier_setting = preservedIdentifierSetting
  }

  return payload
}

export const partitionFieldMappingsForRelocationSave = (
  fieldMappings: any[],
  omsPlatformId: number,
  shippingPlatformId: number,
  omsHubShipmentFieldMappings?: any[] | null
): {omsFieldMappings: any[]; shippingFieldMappings: any[]} => {
  const omsFieldMappings: any[] = []
  const shippingFieldMappings: any[] = []
  const hubShipmentMappings = Array.isArray(omsHubShipmentFieldMappings)
    ? omsHubShipmentFieldMappings
    : []

  fieldMappings.forEach((mapping) => {
    if (isRelocatedShipmentMappingGroup(mapping?.groupCode)) {
      const omsMapping = bindMappingToHubFieldMapping(
        {...mapping, platformId: omsPlatformId},
        hubShipmentMappings
      )
      omsFieldMappings.push(omsMapping)
      return
    }
    shippingFieldMappings.push({...mapping, platformId: shippingPlatformId})
  })

  return {omsFieldMappings, shippingFieldMappings}
}

/**
 * Determines whether the specific order import toggle for OMS integration
 * should be forcefully hidden from the UI.
 *
 * @param groupCode The configuration group code (e.g. 'OMS_ORDER')
 * @param settingName The precise setting name (e.g. 'isImportOrderToSource')
 * @returns boolean True if this is the OMS order setting that must be visually hidden
 */
export const isHiddenOmsOrderToggle = (groupCode: string, settingName: string): boolean => {
  return groupCode === 'OMS_ORDER' && settingName === 'isImportOrderToSource'
}

/**
 * Forcefully intercepts and applies the `isImportOrderToSource = true` state 
 * to the `oms_order_setting` block during initialization.
 * This guarantees the children render in the UI and the next payload saves it as `true`.
 *
 * @param initialValues The Formik initial values tree being built.
 */
export const enforceOmsOrderToggleState = (initialValues: any): void => {
  if (!initialValues || typeof initialValues !== 'object') return

  if (!initialValues.oms_order_setting) {
    initialValues.oms_order_setting = {}
  }
  
  initialValues.oms_order_setting.isImportOrderToSource = true
}
